<template>
  <div class="explore-filters">
    <div class="explore-filters__chips">
      <PlatformChip
        :value="searchFilters.platforms"
        :platforms="userPlatforms"
        :seao-active="seaoActive"
        @change="$emit('platform-change', $event)"
        @clear="onPlatformClear"
        @settings="openSeaoDialog"
      />
      <StatusChip :value="searchFilters.statuses" @change="$emit('status-change', $event)" />
      <ClosingChip :value="searchFilters.closingBefore" @change="$emit('closing-before-change', $event)" />
      <PublicationChip :value="publicationValue" @change="$emit('publication-dates-change', $event)" />
      <ReadChip :value="searchFilters.readFilters" @change="$emit('read-filter-change', $event)" />
      <FollowedChip :value="searchFilters.followFilters" @change="$emit('follow-filter-change', $event)" />
      <SharedChip :value="searchFilters.shareFilters" @change="$emit('share-filter-change', $event)" />
      <AssigneesChip
        :value="searchFilters.assigneesFilters"
        :users="users"
        @change="$emit('assignees-filter-change', $event)"
      />
      <!-- Phones: the advanced filters as a filter line of their own, which opens their dialog. -->
      <FilterChip
        v-if="isPhone"
        is-text
        icon="sliders"
        :label="advancedLabel"
        :active="!!advancedFiltersCount"
        opens-panel
        @toggle="$emit('advanced-filters')"
        @clear="$emit('advanced-clear')"
      />
    </div>

    <div v-if="!isPhone" class="explore-filters__advanced">
      <BaseButton
        hasIconLeft
        isOutlined
        isTransparent
        isSquare
        :class="advancedFiltersCount ? 'active' : ''"
        @click.native="$emit('advanced-filters')"
      >
        <BaseIcon name="sliders" />
        <span class="explore-filters__advanced-text">{{ $t('dialog.filters.advanced.title') }}</span>
        <span v-if="advancedFiltersCount" class="explore-filters__badge">{{ advancedFiltersCount }}</span>
      </BaseButton>
    </div>

    <SEAOFiltersDialog
      v-if="seaoDialogOpen"
      :searchFilters="searchFilters"
      @set-search-filters="onSeaoApply"
      @close-dialog="seaoDialogOpen = false"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import BaseButton from '@/components/base/BaseButton';
import BaseIcon from '@/components/base/BaseIcon';
import FilterChip from '@/components/filters/FilterChip';
import PlatformChip from '@/components/filters/dimensions/PlatformChip';
import StatusChip from '@/components/filters/dimensions/StatusChip';
import ClosingChip from '@/components/filters/dimensions/ClosingChip';
import PublicationChip from '@/components/filters/dimensions/PublicationChip';
import ReadChip from '@/components/filters/dimensions/ReadChip';
import FollowedChip from '@/components/filters/dimensions/FollowedChip';
import SharedChip from '@/components/filters/dimensions/SharedChip';
import AssigneesChip from '@/components/filters/dimensions/AssigneesChip';
import SEAOFiltersDialog from '@/components/page-tenders/SEAOFiltersDialog';

import { advancedFiltersCount, hasSeaoFilters } from '@/util/explore-filters';
import { viewport } from '@/util/viewport';

/**
 * Explore filter bar, fully converged to the presentational chip kit. Owns the page-level data
 * the chips need (platform roster, assignees roster) and the SEAO settings dialog, and re-emits
 * the same events PageExplore already handles. Keywords/categories/zones remain in the advanced
 * dialog (opened via the button here).
 */
export default {
  name: 'ExploreFilters',
  components: {
    BaseButton,
    BaseIcon,
    FilterChip,
    PlatformChip,
    StatusChip,
    ClosingChip,
    PublicationChip,
    ReadChip,
    FollowedChip,
    SharedChip,
    AssigneesChip,
    SEAOFiltersDialog,
  },
  props: {
    searchFilters: {
      type: Object,
      required: true,
    },
  },
  data() {
    return { seaoDialogOpen: false };
  },
  computed: {
    // Anyone can be assigned, so the filter lists everyone, as the inbox and
    // Opportunities filters do. Only BaseAssignation restricts who may be picked.
    ...mapGetters('tenant', ['users']),
    ...mapGetters('userPlatforms', ['userPlatforms']),
    // The DatePicker is fed the range as [start, end] = [publishedAfter, publishedBefore].
    isPhone() {
      return viewport.size === 'phone';
    },
    advancedLabel() {
      const label = this.$t('dialog.filters.advanced.title');
      return this.advancedFiltersCount ? `${label} (${this.advancedFiltersCount})` : label;
    },
    publicationValue() {
      return [this.searchFilters.publishedAfter, this.searchFilters.publishedBefore].filter(Boolean);
    },
    // SEAO sub-filters (regions/classifications) active → show the active dot on the platform gear.
    seaoActive() {
      return hasSeaoFilters(this.searchFilters || {});
    },
    advancedFiltersCount() {
      return advancedFiltersCount(this.searchFilters || {}) || '';
    },
  },
  methods: {
    ...mapActions('userPlatforms', ['getUserPlatforms']),
    openSeaoDialog() {
      this.seaoDialogOpen = true;
    },
    onSeaoApply(filters) {
      this.$emit('seao-filters-change', filters);
      this.seaoDialogOpen = false;
    },
    // Clearing the platform chip also clears its SEAO sub-filters (matches the legacy clear-all).
    onPlatformClear() {
      this.$emit('seao-filters-change', { regions: [], classifications: [] });
    },
  },
  mounted() {
    this.getUserPlatforms();
  },
};
</script>

<style lang="scss">
.explore-filters {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 0;

  &__chips {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    flex: 1 1 auto;
    min-width: 0;
  }

  &__advanced {
    display: flex;
    margin-left: auto;

    button {
      background: transparent;
      color: $color-normal;
      border-radius: $border-radius-root;

      &:hover {
        color: $color-normal;
        background-color: $color-hover;
        border: none !important;
      }
    }
  }

  &__advanced-text {
    font-size: $font-size-root;
    white-space: nowrap;
  }

  &__badge {
    background-color: $color-accent;
    border-radius: $border-radius-round;
    color: $color-white;
    font-size: $font-size-xxsmall;
    font-weight: bold;
    margin-left: 10px;
    padding: 3px 4px;
    height: $badge-notification-size;
    width: $badge-notification-size;
    line-height: 15px;
    display: flex;
    justify-content: center;
    position: relative;
    left: -3px;
  }

  @media screen and (max-width: 1436px) {
    &__advanced-text {
      display: none;
    }
  }
}
</style>
